import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OrganizationsNavigationProvider } from '../../../../organizations/providers/navigation-provider';
import { CurrentUserRolesProvider } from '../../../../organizations/providers/current-user-roles-provider';
import { HeaderRights } from '../header-rights';
import { Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { Organization } from "../../../models/organization";
import { CurrentUser } from "../../../providers/current-user";
import { UtilsProvider } from "../../../providers/utils-provider.service";
import { ConfigProvider } from "../../../../config-provider.service";
import { OrganizationMembership } from "../../../models/organization-membership";
import { catchError, map } from "rxjs/operators";
import { OrganizationRole } from "../../../models/organization-role";
import { Coach } from "../../../models/coach";
import { NgIf } from "@angular/common";
import { MatButton, MatMiniFabButton } from "@angular/material/button";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { MatIcon } from "@angular/material/icon";
import { AthletesProvider } from "../../../../athletes/providers/athletes-provider";
import { CoachesProvider } from "../../../../coaches/providers/coaches-provider";
import { Athlete } from "../../../models/athlete";
import { User } from "../../../models/user";
import { consumerAfterComputation } from "@angular/core/primitives/signals";

@Component({
  selector: 'header-main-menus',
  providers: [],
  styleUrls: ['./main-menus.scss'],
  standalone: true,
  imports: [
    NgIf,
    RouterLink,
    MatButton,
    TranslateModule,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatMiniFabButton,
    MatIcon
  ],
  templateUrl: './main-menus.html'
})
export class HeaderMainMenusComponent implements AfterViewInit, OnInit, OnDestroy {
    public readonly MENU_DASHBOARD: string = 'dashboard';
    public readonly MENU_LIBRARIES: string = 'libraries';
    public readonly MENU_TEAMS: string = 'teams';
    public readonly MENU_SUPERCOACHS: string = 'super-coaches';
    public readonly MENU_COACHS: string = 'coaches';
    public readonly MENU_ATHLETES: string = 'athletes';
    public readonly MENU_GUEST: string = 'guests';
    public organization: Organization | null = null;
    public currentMenu: string | null = null;
    public rights: HeaderRights = new HeaderRights(this.utils);
    public dashboardUrl = '/organizations';
    private subscriptions: Subscription[] = [];
    constructor(
        public conf: ConfigProvider,
        public utils: UtilsProvider,
        public currentUser: CurrentUser,
        private _translater: TranslateService,
        private _changeDetector: ChangeDetectorRef,
        private _route: ActivatedRoute,
        private _organizationNavigation: OrganizationsNavigationProvider,
        private _currentUserRoles: CurrentUserRolesProvider,
        private _athletesProvider: AthletesProvider,
        private _coachesProvider: CoachesProvider,
    ) {
    }

  public ngAfterViewInit() {
      this._changeDetector.detectChanges();
  }

  public ngOnInit() {
    this.subscriptions.push(
      this._route.params.subscribe((params: any) => {
        // le "currentMenu" vient toujours juste après le code de l'organisation
        this.currentMenu = this._route.snapshot.url.length >= 2 ? this._route.snapshot.url[1].path : null;

        this.subscriptions.push(
          this.currentUser.getOrganizationsMemberships().pipe(
            switchMap((memberships: OrganizationMembership[]) => {
              if (memberships && memberships.length === 1) {
                this.organization = memberships[0].organization!;
                if (this.organization.id) {
                  const roles = this._currentUserRoles.getRolesIds(this.organization.id);
                  this.rights.evaluateScreenRights({ rolesIds: roles });

                  return this.currentUser.getMainHomeUrl(this.organization, roles).pipe(
                    tap((url: string) => {
                      this.dashboardUrl = url;
                      this.adjustCurrentMenu(roles);
                    })
                  );
                } else {
                  return of(null); // Return a null observable when no organization.id is present
                }
              } else {
                return this._organizationNavigation.getOrganisationFromRoute().pipe(
                  tap((organization: Organization|null) => {
                    this.organization = organization;
                    if (this.organization?.id) {
                      const roles = this._currentUserRoles.getRolesIds(this.organization.id);
                      this.rights.evaluateScreenRights({ rolesIds: roles });

                      return this.currentUser.getMainHomeUrl(this.organization, roles).subscribe((url: string) => {
                        this.dashboardUrl = url;
                        this.adjustCurrentMenu(roles);
                      });
                    }
                    return of(null);
                  }),
                  catchError(() => of('Default URL or error handling logic'))
                );
              }
            }),
            catchError(err => {
              console.error('Error fetching organization memberships', err);
              return of(null);  // Handle error or provide a fallback logic
            })
          ).subscribe()
        );
      })
    );
  }


  public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

  private adjustCurrentMenu(roles: number[]) {

      /**
       * Si je suis un super-coach et que je visite le lien .../super-coaches/<mon-super-coach-id>, je
       * suis sur mon dashboard, idem si je visite la page .../coaches/<mon-super-coach-id>.
       *
       * Si je suis un coach et que je visite le lien .../coaches/<mon-super-coach-id>, je
       * suis sur mon dashboard.
       *
       * Si je suis un athlète et que je visite le lien .../athletes/<mon-athlete-id>, je
       * suis sur mon dashboard.
       *
       * C'est absolument immonde !
       */
      if (roles.indexOf(OrganizationRole.ID_SUPERCOACH) > -1) {
        this._coachesProvider.get(this.organization!.id!, this.currentUser.user!.id!).subscribe({
          next: (coachUser: User) => {
            const coachId = this._route.snapshot.params["coachId"] ? parseInt(this._route.snapshot.params["coachId"], 10) : 0;
            if (this.currentMenu === this.MENU_SUPERCOACHS && coachUser && coachId === coachUser.id) {

                // je suis sur mon dashboard
                this.currentMenu = this.MENU_DASHBOARD;
            }
          },
          error: this.onError
        });
      } else if (roles.indexOf(OrganizationRole.ID_COACH) > -1) {
        this._coachesProvider.get(this.organization!.id!, this.currentUser.user!.id!).subscribe({
          next: (coachUser: User) => {
            const coachId = this._route.snapshot.params["coachId"] ? parseInt(this._route.snapshot.params["coachId"], 10) : 0;
            if (this.currentMenu === this.MENU_COACHS && coachUser && coachId === coachUser.id) {

                // je suis sur mon dashboard
                this.currentMenu = this.MENU_DASHBOARD;
            }
          },
          error: this.onError
        });
      } else if (roles.indexOf(OrganizationRole.ID_ATHLETE) > -1) {
        this._athletesProvider.get(this.organization!.id!, this.currentUser.user!.id!).subscribe({
          next: (athleteUser: User) => {
            const athleteId = this._route.snapshot.params["athleteId"] ? parseInt(this._route.snapshot.params["athleteId"], 10) : 0;
            if (this.currentMenu === this.MENU_ATHLETES && athleteUser && athleteId === athleteUser.id) {

                // je suis sur mon dashboard
                this.currentMenu = this.MENU_DASHBOARD;
            }
          },
          error: this.onError
        });
      }
  }

  private onError(error: any): void {
    console.error("Main Menu : ", error);
  }
}
